import { REPORT_TYPES_SEED, SSE_EVENTS } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Button, Group, Modal } from "@mantine/core"
import { notifications } from "@mantine/notifications"
import { IconFileTypeHtml, IconFileTypePdf } from "@tabler/icons-react"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useAuth } from "../../hooks/useAuth"
import { useCompanyMutation, useCompanyQuery } from "../../hooks/useCompanyQuery"
import { createReconnectingEventSource } from "../../hooks/useSSE"
import {
  fetchReportEstimate,
  fetchReportFit,
  fetchReportTypes,
  fetchVisualReports,
  generateVisualReport,
  visualReportHtmlUrl,
} from "../../lib/api"
import { extractErrorMessage } from "../../lib/api/errors"
import { notifyError } from "../../lib/notify"
import { queryKeys } from "../../lib/query-keys"
import { ReportOfferBanner } from "../ReportOfferBanner"
import { ReportViewer } from "../ReportViewer"
import type { IProps } from "./interfaces"

import classes from "./styles.module.css"

function isString(value: unknown): value is string {
  return typeof value === "string"
}

/** Pull the server-chosen filename out of a `Content-Disposition: attachment` header. */
function filenameFromDisposition(header: string | null): string | null {
  const match = header?.match(/filename="([^"]+)"/)
  return match ? match[1] : null
}

/**
 * MeetingReportBlock — smart wrapper for the visual-report offer + viewer.
 *
 * Owns the React Query graph (report-fit + generated-reports + a cost estimate),
 * the generate KICKOFF mutation, the live generation state, and the viewer modal.
 * Generation is asynchronous: the POST only starts a detached backend task and
 * returns immediately, so progress is driven by the `report_generation_*` SSE
 * events (filtered to this meeting) rather than by the mutation's resolution.
 * Renders the dumb ReportOfferBanner and, on view, a sandboxed ReportViewer.
 *
 * Renders `null` when the meeting isn't report-worthy and nothing is generated,
 * so the summary layout drops the block entirely (its renderBlock cell filters
 * nulls). The two dumb halves (banner + viewer) carry the stories; this smart
 * wrapper does not.
 */
export function MeetingReportBlock({ meetingId, companyId }: IProps): React.JSX.Element | null {
  // STATE
  const [viewingArtifactId, setViewingArtifactId] = useState<number | null>(null)
  // The report type currently generating (drives the banner spinner). Set locally
  // on click AND by the STARTED SSE event, so every viewer sees live progress.
  const [generatingType, setGeneratingType] = useState<string | null>(null)
  // Whether the user has opted the next generation up to the best (Opus) model.
  const [useBestModel, setUseBestModel] = useState<boolean>(false)
  // Which download is in flight (drives the button spinners; PDF takes seconds).
  const [downloadingFormat, setDownloadingFormat] = useState<"html" | "pdf" | null>(null)

  // EXTERNAL HOOKS
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const { i18n } = useLingui()

  // The set of types THIS client kicked off — gates the auto-open-viewer + failure
  // toast to the initiator, so a passive viewer of the same meeting isn't hijacked
  // by a modal (or an error toast) for a generation someone else triggered.
  const initiatedTypesRef = useRef<Set<string>>(new Set())

  const fitQuery = useCompanyQuery({
    key: (org) => queryKeys.reportFit(org, meetingId),
    queryFn: (company) => fetchReportFit(company, meetingId),
    staleTime: 5 * 60 * 1000,
  })

  const reportsQuery = useCompanyQuery({
    key: (org) => queryKeys.visualReports(org, meetingId),
    queryFn: (company) => fetchVisualReports(company, meetingId),
  })

  // DERIVED STATE
  const fit = fitQuery.data ?? null
  const suggestedType = fit?.suggestedType ?? null
  const generated = useMemo(
    () =>
      (reportsQuery.data ?? []).map((r) => ({
        artifactId: r.id,
        reportType: r.meta.reportType,
        title: r.meta.title,
      })),
    [reportsQuery.data],
  )

  // The cost estimate only matters in the "suggested" state — when there IS a
  // suggestion that hasn't been generated yet. Skip the call otherwise.
  const needsEstimate = !!suggestedType && !generated.some((g) => g.reportType === suggestedType)
  const estimateQuery = useCompanyQuery({
    key: (org) => queryKeys.reportEstimate(org, meetingId, suggestedType),
    queryFn: (company) => {
      if (!suggestedType) throw new Error("estimate unavailable")
      return fetchReportEstimate(company, meetingId, suggestedType)
    },
    enabled: needsEstimate,
    staleTime: 5 * 60 * 1000,
  })

  // The kickoff mutation only STARTS generation; it doesn't carry the artifact.
  // Success is a no-op (the SSE events drive the UI). A rejected kickoff (no
  // dialogue, or a concurrent-generation 409) clears the spinner + surfaces the
  // error here; generation-time failures arrive via the FAILED SSE event instead.
  const generateMutation = useCompanyMutation({
    mutationFn: (company, vars: { type: string; tier: "standard" | "best" }) =>
      generateVisualReport(company, meetingId, vars.type, vars.tier),
    onError: (err, vars) => {
      initiatedTypesRef.current.delete(vars.type)
      setGeneratingType((current) => (current === vars.type ? null : current))
      notifyError(err, i18n._(msg`Report generation failed`))
    },
  })

  // Report types offered in the "Choose another" / "Generate another" menus —
  // the per-org RESOLVED set (WS5), so this workspace's enable/disable + custom
  // types are reflected. Falls back to the enabled platform seed while the query
  // loads (or if it fails), so the menus are never empty.
  const reportTypesQuery = useCompanyQuery({
    key: queryKeys.reportTypes,
    queryFn: (company) => fetchReportTypes(company),
    staleTime: 5 * 60 * 1000,
  })
  const reportTypes = useMemo(() => {
    const resolved = reportTypesQuery.data?.types
    const source = resolved && resolved.length > 0 ? resolved : REPORT_TYPES_SEED
    return source.filter((t) => t.enabled).map((t) => ({ id: t.id, label: t.label }))
  }, [reportTypesQuery.data])

  // Per-tier credit cost (already the real marked-up debit, server-side) + whether
  // this workspace offers the best model at all.
  const estimate = estimateQuery.data ?? null
  const estimateCredits = estimate?.standard.credits ?? null
  const bestEstimateCredits = estimate?.best.credits ?? null
  const bestAllowed = estimate?.bestAllowed ?? false

  // CALLBACKS
  const handleGenerate = useCallback(
    (type: string): void => {
      initiatedTypesRef.current.add(type)
      setGeneratingType(type)
      // Only send "best" when the workspace still allows it; then reset the toggle
      // so a second generation defaults back to standard (a conscious opt-in each time).
      const tier: "standard" | "best" = useBestModel && bestAllowed ? "best" : "standard"
      generateMutation.mutate({ type, tier })
      setUseBestModel(false)
    },
    [generateMutation, useBestModel, bestAllowed],
  )
  const handleToggleBestModel = useCallback((on: boolean): void => setUseBestModel(on), [])
  const handleView = useCallback((artifactId: number): void => setViewingArtifactId(artifactId), [])
  const closeViewer = useCallback((): void => setViewingArtifactId(null), [])
  // Download the currently-open report as a self-contained HTML file or a
  // server-printed PDF. Fetched as a blob instead of a hidden-anchor navigation so
  // the button can show a live loading state — the PDF is rendered server-side by
  // Chromium and takes a few seconds, and a fire-and-forget anchor reads as a dead
  // click. A plain same-origin fetch (not Eden) for the same reason the viewer
  // iframe uses a plain URL: the endpoint returns a raw file, cookies ride along.
  const handleDownload = useCallback(
    (format: "html" | "pdf"): void => {
      if (viewingArtifactId === null || downloadingFormat !== null) return
      const url = visualReportHtmlUrl(companyId, meetingId, viewingArtifactId, {
        download: true,
        ...(format === "pdf" ? { format } : {}),
      })
      setDownloadingFormat(format)
      void (async (): Promise<void> => {
        try {
          const response = await fetch(url, { credentials: "same-origin" })
          if (!response.ok) {
            // Error bodies are the API's JSON shape — surface the real message.
            const body: unknown = await response.json().catch(() => null)
            throw new Error(body === null ? `Download failed (${response.status})` : extractErrorMessage(body))
          }
          const blob = await response.blob()
          const objectUrl = URL.createObjectURL(blob)
          const a = document.createElement("a")
          a.href = objectUrl
          a.download = filenameFromDisposition(response.headers.get("content-disposition")) ?? `report.${format}`
          a.rel = "noopener"
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          URL.revokeObjectURL(objectUrl)
        } catch (err) {
          notifyError(err, i18n._(msg`Download failed`))
        } finally {
          setDownloadingFormat(null)
        }
      })()
    },
    [companyId, meetingId, viewingArtifactId, downloadingFormat, i18n],
  )
  const handleDownloadHtml = useCallback((): void => handleDownload("html"), [handleDownload])
  const handleDownloadPdf = useCallback((): void => handleDownload("pdf"), [handleDownload])

  // EFFECTS — subscribe to the report-generation SSE lifecycle for THIS meeting.
  useEffect(() => {
    if (!orgId) return

    const handleMessage = (event: MessageEvent<string>): void => {
      try {
        const parsed: unknown = JSON.parse(event.data)
        if (typeof parsed !== "object" || parsed === null) return
        const data = parsed as Record<string, unknown>
        if (data.meetingId !== meetingId) return
        const reportType = isString(data.reportType) ? data.reportType : null

        switch (data.type) {
          case SSE_EVENTS.REPORT_GENERATION_STARTED:
            if (reportType) setGeneratingType(reportType)
            break

          case SSE_EVENTS.REPORT_GENERATION_COMPLETE: {
            setGeneratingType((current) => (current === reportType ? null : current))
            queryClient.invalidateQueries({ queryKey: queryKeys.visualReports(orgId, meetingId) })
            // Only the initiator pops the viewer open; passive viewers just see
            // the report land in the banner's "ready" row.
            if (reportType && initiatedTypesRef.current.has(reportType)) {
              initiatedTypesRef.current.delete(reportType)
              if (typeof data.artifactId === "number") setViewingArtifactId(data.artifactId)
            }
            break
          }

          case SSE_EVENTS.REPORT_GENERATION_FAILED:
            setGeneratingType((current) => (current === reportType ? null : current))
            // Surface the failure only to the client that asked for it.
            if (reportType && initiatedTypesRef.current.has(reportType)) {
              initiatedTypesRef.current.delete(reportType)
              notifications.show({
                message: isString(data.error) ? data.error : i18n._(msg`Report generation failed`),
                color: "danger",
              })
            }
            break

          default:
            break
        }
      } catch {
        // ignore parse errors
      }
    }

    return createReconnectingEventSource(`/api/companies/${orgId}/events`, false, handleMessage)
  }, [orgId, meetingId, queryClient, i18n])

  // GUARD — mirror the banner's own hide condition so no empty Modal mounts and
  // the summary layout drops the block when there's nothing to show. A generation
  // in flight keeps the block mounted even before the first artifact exists.
  if (!fit?.eligible || (!suggestedType && generated.length === 0 && generatingType === null)) return null

  const viewedTitle = generated.find((g) => g.artifactId === viewingArtifactId)?.title

  // RENDER
  return (
    <>
      <ReportOfferBanner
        fit={fit}
        reportTypes={reportTypes}
        generated={generated}
        estimateCredits={estimateCredits}
        bestAllowed={bestAllowed}
        bestEstimateCredits={bestEstimateCredits}
        useBestModel={useBestModel}
        onToggleBestModel={handleToggleBestModel}
        isGenerating={generatingType !== null}
        generatingType={generatingType}
        onGenerate={handleGenerate}
        onView={handleView}
      />
      <Modal.Root opened={viewingArtifactId !== null} onClose={closeViewer} size="90%">
        <Modal.Overlay />
        <Modal.Content>
          <Modal.Header>
            <Modal.Title className={classes.title}>{viewedTitle ?? i18n._(msg`Visual report`)}</Modal.Title>
            <Group gap="xs" wrap="nowrap">
              <Button
                size="compact-sm"
                variant="light"
                leftSection={<IconFileTypeHtml size={14} />}
                loading={downloadingFormat === "html"}
                disabled={downloadingFormat === "pdf"}
                onClick={handleDownloadHtml}
                aria-label={i18n._(msg`Download HTML`)}
              >
                <Trans>HTML</Trans>
              </Button>
              <Button
                size="compact-sm"
                variant="light"
                leftSection={<IconFileTypePdf size={14} />}
                loading={downloadingFormat === "pdf"}
                disabled={downloadingFormat === "html"}
                onClick={handleDownloadPdf}
                aria-label={i18n._(msg`Download PDF`)}
              >
                <Trans>PDF</Trans>
              </Button>
              <Modal.CloseButton />
            </Group>
          </Modal.Header>
          <Modal.Body>
            {viewingArtifactId !== null && (
              <ReportViewer src={visualReportHtmlUrl(companyId, meetingId, viewingArtifactId)} title={viewedTitle} />
            )}
          </Modal.Body>
        </Modal.Content>
      </Modal.Root>
    </>
  )
}
